/**
 * GeometryPoints - Represents geometric point information with coordinates, rotation, and angles.
 * Used to store point coordinates, rotation values, and angle references for geometric elements.
 * 
 * NOTE: Property names in this class match the external data source schema.
 * They use PascalCase to maintain direct mapping compatibility.
 */
export class GeometryPoints {
  /** Point coordinates as an object with x and y values */
  Point: { x: number; y: number };

  /** X coordinate */
  X: number;

  /** Y coordinate */
  Y: number;

  /** Rotation value */
  Rotation: number;

  /** Rotation reference identifier */
  RotationReference: string;

  /** Absolute angle value */
  AbsoluteAngle: number;

  /**
   * Creates a new GeometryPoints instance
   * @param data - Object containing geometry points properties
   */
  constructor(data: {
    Point: { x: number; y: number };
    X: number;
    Y: number;
    Rotation: number;
    RotationReference: string;
    AbsoluteAngle: number;
  }) {
    this.Point = data.Point;
    this.X = data.X;
    this.Y = data.Y;
    this.Rotation = data.Rotation;
    this.RotationReference = data.RotationReference;
    this.AbsoluteAngle = data.AbsoluteAngle;
  }

  /**
   * Creates a GeometryPoints from a plain object (useful for deserialization)
   * @param obj - Plain object with geometry points data
   * @returns New GeometryPoints instance
   */
  static fromObject(obj: Partial<GeometryPoints>): GeometryPoints {
    let point: { x: number; y: number };
    if (obj.Point && typeof obj.Point === "object" && "x" in obj.Point && "y" in obj.Point) {
      point = {
        x: typeof obj.Point.x === "number" ? obj.Point.x : 0,
        y: typeof obj.Point.y === "number" ? obj.Point.y : 0,
      };
    } else {
      point = { x: 0, y: 0 };
    }

    return new GeometryPoints({
      Point: point,
      X: obj.X ?? 0,
      Y: obj.Y ?? 0,
      Rotation: obj.Rotation ?? 0,
      RotationReference: obj.RotationReference ?? "",
      AbsoluteAngle: obj.AbsoluteAngle ?? 0,
    });
  }

  /**
   * Converts the GeometryPoints to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      Point: this.Point,
      X: this.X,
      Y: this.Y,
      Rotation: this.Rotation,
      RotationReference: this.RotationReference,
      AbsoluteAngle: this.AbsoluteAngle,
    };
  }
}

